﻿import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useCart } from "../../hooks/useCart";

const navLinks = [
    { label: "Home", to: "/" },
    { label: "Shop", to: "/shop" },
    { label: "Contact", to: "/contact" },
];

export default function Header() {
    const { isLoggedIn, user, logout } = useAuth();
    const { cartCount } = useCart();
    const navigate = useNavigate();

    const [menuOpen, setMenuOpen] = useState(false);
    const [accountOpen, setAccountOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const accountRef = useRef(null);
    const searchRef = useRef(null);

    // Shadow on scroll
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Close account dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (accountRef.current && !accountRef.current.contains(e.target)) {
                setAccountOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Close search on outside click
    useEffect(() => {
        const handler = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setSearchOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Close mobile menu on resize
    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth >= 768) setMenuOpen(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const handleLogout = () => {
        logout();
        setAccountOpen(false);
        setMenuOpen(false);
        navigate("/");
    };

    const activeLinkClass = ({ isActive }) =>
        isActive
            ? "text-secondary-600 font-semibold after:scale-x-100"
            : "text-neutral-600 hover:text-secondary-600 transition-colors duration-200";

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [menuOpen]);

    return (
        <>
            <header
                className={`sticky top-0 z-50 bg-white/95 backdrop-blur-md transition-all duration-300 ${
                    scrolled
                        ? "shadow-lg shadow-neutral-900/5 border-b border-neutral-100"
                        : "border-b border-neutral-100/60"
                }`}
            >
                <div className="container flex items-center justify-between h-16 md:h-20 gap-4">
                    {/* Logo */}
                    <Link
                        to="/"
                        className="flex items-center gap-2.5 shrink-0 group"
                    >
                        {/* Logo mark - stylized diamond F */}
                        <div className="relative w-9 h-9 bg-gradient-to-br from-secondary-500 to-secondary-700 rounded-xl flex items-center justify-center shadow-sm shadow-secondary-500/20 group-hover:shadow-md group-hover:shadow-secondary-500/30 transition-shadow duration-300">
                            <svg
                                viewBox="0 0 24 24"
                                className="w-5 h-5 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2.2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 2L20 12L12 22L4 12Z" />
                                <path d="M8 12H16" />
                                <path d="M12 8V16" />
                            </svg>
                        </div>
                        {/* Brand name */}
                        <span className="text-xl font-extrabold tracking-tight">
                            <span className="text-neutral-900">Fits</span>
                            <span className="text-secondary-600">Aura</span>
                        </span>
                    </Link>

                    {/* Desktop Nav */}
                    <nav className="hidden md:flex items-center gap-1">
                        {navLinks.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                end={link.to === "/"}
                                className={({ isActive }) =>
                                    "relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 " +
                                    (isActive
                                        ? "text-secondary-600 bg-secondary-50"
                                        : "text-neutral-600 hover:text-secondary-600 hover:bg-neutral-50")
                                }
                            >
                                {link.label}
                            </NavLink>
                        ))}
                    </nav>

                    {/* Right Actions */}
                    <div className="flex items-center gap-1.5 ml-auto md:ml-0">
                        {/* Search toggle - Desktop */}
                        <button
                            onClick={() => setSearchOpen(!searchOpen)}
                            className="hidden md:flex p-2.5 rounded-xl text-neutral-500 hover:text-secondary-600 hover:bg-secondary-50 transition-all duration-200"
                            aria-label="Search"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                                />
                            </svg>
                        </button>

                        {/* Cart Button */}
                        <Link
                            to="/cart"
                            className="relative p-2.5 rounded-xl text-neutral-500 hover:text-secondary-600 hover:bg-secondary-50 transition-all duration-200"
                            aria-label="Cart"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-1.5 6h13M7 13L5.4 5M10 21a1 1 0 100-2 1 1 0 000 2zm7 0a1 1 0 100-2 1 1 0 000 2z"
                                />
                            </svg>
                            {cartCount > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-secondary-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1.5 ring-2 ring-white shadow-sm">
                                    {cartCount > 99 ? "99+" : cartCount}
                                </span>
                            )}
                        </Link>

                        {/* Account - Desktop */}
                        <div
                            className="relative hidden md:block"
                            ref={accountRef}
                        >
                            {isLoggedIn ? (
                                <>
                                    <button
                                        onClick={() =>
                                            setAccountOpen((prev) => !prev)
                                        }
                                        className="flex items-center gap-2 pl-2.5 pr-3 py-2 rounded-xl text-sm font-medium text-neutral-600 hover:text-secondary-600 hover:bg-secondary-50 transition-all duration-200"
                                    >
                                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-secondary-400 to-secondary-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                                            {user?.name
                                                ?.charAt(0)
                                                ?.toUpperCase() || "U"}
                                        </div>
                                        <span className="max-w-[90px] truncate">
                                            {user?.name?.split(" ")[0] ||
                                                "Account"}
                                        </span>
                                        <ChevronIcon open={accountOpen} />
                                    </button>

                                    {/* Dropdown */}
                                    {accountOpen && (
                                        <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-neutral-200 rounded-2xl shadow-xl shadow-neutral-900/10 py-2 z-50 origin-top-right">
                                            <div className="px-5 py-3 border-b border-neutral-100">
                                                <p className="text-xs text-neutral-400">
                                                    Signed in as
                                                </p>
                                                <p className="text-sm font-semibold text-neutral-800 truncate mt-0.5">
                                                    {user?.email}
                                                </p>
                                            </div>
                                            <div className="py-1">
                                                <DropdownLink
                                                    to="/my-account"
                                                    onClick={() =>
                                                        setAccountOpen(false)
                                                    }
                                                >
                                                    <span>&#x1f3e0;</span>
                                                    My Account
                                                </DropdownLink>
                                                <DropdownLink
                                                    to="/my-account/orders"
                                                    onClick={() =>
                                                        setAccountOpen(false)
                                                    }
                                                >
                                                    <span>&#x1f4e6;</span>
                                                    My Orders
                                                </DropdownLink>
                                                <DropdownLink
                                                    to="/my-account/profile"
                                                    onClick={() =>
                                                        setAccountOpen(false)
                                                    }
                                                >
                                                    <span>&#x1f464;</span>
                                                    Account Details
                                                </DropdownLink>
                                            </div>
                                            <hr className="my-1 border-neutral-100 mx-3" />
                                            <div className="py-1">
                                                <button
                                                    onClick={handleLogout}
                                                    className="flex items-center gap-2.5 w-full text-left px-5 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors font-medium"
                                                >
                                                    <span>&#x1f6aa;</span>
                                                    Sign Out
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Link
                                        to="/login"
                                        className="text-sm font-medium text-neutral-600 hover:text-secondary-600 transition-colors duration-200 px-3 py-2"
                                    >
                                        Sign In
                                    </Link>
                                    <Link
                                        to="/register"
                                        className="bg-secondary-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-secondary-700 transition-all duration-200 shadow-sm shadow-secondary-500/20 hover:shadow-md hover:shadow-secondary-500/30"
                                    >
                                        Create Account
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Hamburger - Mobile */}
                        <button
                            type="button"
                            className="md:hidden relative w-10 h-10 flex items-center justify-center rounded-xl text-neutral-500 hover:text-secondary-600 hover:bg-secondary-50 transition-all duration-200"
                            onClick={() => setMenuOpen((prev) => !prev)}
                            aria-label="Toggle menu"
                        >
                            <div className="relative w-5 h-5">
                                <span
                                    className={`absolute left-0 top-0.5 w-full h-[2.5px] rounded-full bg-current transition-all duration-300 ${
                                        menuOpen
                                            ? "rotate-45 translate-y-[7px]"
                                            : ""
                                    }`}
                                />
                                <span
                                    className={`absolute left-0 top-[9px] w-full h-[2.5px] rounded-full bg-current transition-all duration-300 ${
                                        menuOpen ? "opacity-0 scale-0" : ""
                                    }`}
                                />
                                <span
                                    className={`absolute left-0 bottom-0.5 w-full h-[2.5px] rounded-full bg-current transition-all duration-300 ${
                                        menuOpen
                                            ? "-rotate-45 -translate-y-[7px]"
                                            : ""
                                    }`}
                                />
                            </div>
                        </button>
                    </div>
                </div>

                {/* Search Bar */}
                {searchOpen && (
                    <div ref={searchRef} className="container pb-4 md:pb-5">
                        <form
                            action="/shop"
                            method="GET"
                            className="relative"
                            onSubmit={(e) => {
                                e.preventDefault();
                                const form = new FormData(e.target);
                                const q = form.get("q");
                                if (q?.trim()) {
                                    navigate(
                                        "/shop?search=" +
                                            encodeURIComponent(q.trim()),
                                    );
                                    setSearchOpen(false);
                                }
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                                />
                            </svg>
                            <input
                                type="text"
                                name="q"
                                placeholder="Search products..."
                                className="w-full pl-12 pr-12 py-3.5 bg-neutral-50 border-2 border-neutral-200 rounded-2xl text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-secondary-400 focus:bg-white focus:ring-4 focus:ring-secondary-100/60 transition-all duration-200"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => setSearchOpen(false)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-200 transition-all"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </form>
                    </div>
                )}
            </header>

            {/* Mobile Menu Overlay (outside sticky header) */}
            {menuOpen && (
                <div
                    className="fixed inset-0 top-0 z-[100] md:hidden"
                    onClick={(e) =>
                        e.target === e.currentTarget && setMenuOpen(false)
                    }
                >
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
                        onClick={() => setMenuOpen(false)}
                    />

                    {/* Menu Panel */}
                    <div className="relative ml-auto w-full max-w-sm bg-white h-full overflow-y-auto shadow-2xl shadow-neutral-900/20">
                        <div className="flex flex-col min-h-full">
                            {/* Close button */}
                            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
                                <p className="text-sm font-bold text-neutral-900">
                                    Menu
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setMenuOpen(false)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-all"
                                    aria-label="Close menu"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2.5}
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>
                            {/* User section */}
                            <div className="px-5 pt-6 pb-4 border-b border-neutral-100">
                                {isLoggedIn ? (
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-secondary-400 to-secondary-600 flex items-center justify-center text-white text-lg font-bold shadow-md shrink-0">
                                            {user?.name
                                                ?.charAt(0)
                                                ?.toUpperCase() || "U"}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-bold text-neutral-900 truncate">
                                                {user?.name || "User"}
                                            </p>
                                            <p className="text-xs text-neutral-400 truncate">
                                                {user?.email}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        <p className="text-sm text-neutral-500 font-medium">
                                            Discover Your Style
                                        </p>
                                        <div className="flex gap-2">
                                            <Link
                                                to="/login"
                                                onClick={() =>
                                                    setMenuOpen(false)
                                                }
                                                className="flex-1 text-center text-sm font-semibold py-3 rounded-xl border-2 border-secondary-600 text-secondary-600 hover:bg-secondary-50 transition-all"
                                            >
                                                Sign In
                                            </Link>
                                            <Link
                                                to="/register"
                                                onClick={() =>
                                                    setMenuOpen(false)
                                                }
                                                className="flex-1 text-center text-sm font-semibold py-3 rounded-xl bg-secondary-600 text-white hover:bg-secondary-700 transition-all"
                                            >
                                                Register
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Nav links */}
                            <nav className="flex-1 px-3 py-4">
                                <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider px-3 pb-2">
                                    Navigation
                                </div>
                                {navLinks.map((link) => (
                                    <NavLink
                                        key={link.to}
                                        to={link.to}
                                        end={link.to === "/"}
                                        onClick={() => setMenuOpen(false)}
                                        className={({ isActive }) =>
                                            "flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all " +
                                            (isActive
                                                ? "bg-secondary-50 text-secondary-600 font-semibold"
                                                : "text-neutral-700 hover:bg-neutral-50")
                                        }
                                    >
                                        {link.label}
                                    </NavLink>
                                ))}

                                {/* Quick search */}
                                <div className="mt-4 px-3">
                                    <form
                                        action="/shop"
                                        method="GET"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            const form = new FormData(e.target);
                                            const q = form.get("q");
                                            if (q?.trim()) {
                                                navigate(
                                                    "/shop?search=" +
                                                        encodeURIComponent(
                                                            q.trim(),
                                                        ),
                                                );
                                                setMenuOpen(false);
                                            }
                                        }}
                                    >
                                        <div className="relative">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth={2}
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                                                />
                                            </svg>
                                            <input
                                                type="text"
                                                name="q"
                                                placeholder="Search..."
                                                className="w-full pl-9 pr-3 py-3 bg-neutral-100 rounded-xl text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:bg-white transition-all"
                                            />
                                        </div>
                                    </form>
                                </div>
                            </nav>

                            {/* Logged in footer */}
                            {isLoggedIn && (
                                <div className="border-t border-neutral-100 px-3 py-4">
                                    <Link
                                        to="/my-account"
                                        onClick={() => setMenuOpen(false)}
                                        className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-all"
                                    >
                                        <span>&#x1f3e0;</span>
                                        My Account
                                    </Link>
                                    <Link
                                        to="/my-account/orders"
                                        onClick={() => setMenuOpen(false)}
                                        className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-all"
                                    >
                                        <span>&#x1f4e6;</span>
                                        My Orders
                                    </Link>
                                    <button
                                        onClick={handleLogout}
                                        className="flex items-center gap-3 w-full px-3 py-3 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition-all mt-1"
                                    >
                                        <span>&#x1f6aa;</span>
                                        Sign Out
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// Small Internal Components

function DropdownLink({ to, onClick, children }) {
    return (
        <Link
            to={to}
            onClick={onClick}
            className="flex items-center gap-2.5 px-5 py-2.5 text-sm text-neutral-700 hover:bg-secondary-50 hover:text-secondary-600 transition-colors font-medium"
        >
            {children}
        </Link>
    );
}

function ChevronIcon({ open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className={
                "w-3.5 h-3.5 transition-transform duration-200 " +
                (open ? "rotate-180" : "")
            }
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
            />
        </svg>
    );
}
